/**
 * useIsMobile.js
 *
 * Reactive hook that tracks whether the viewport is at/below a mobile
 * breakpoint. Used to decide when to show on-screen touch controls for
 * the game instead of relying on keyboard/mouse only.
 *
 * Uses matchMedia (not just a resize listener) so it also responds
 * correctly to orientation changes on mobile devices.
 *
 * @param {number} breakpoint  max-width in px counted as "mobile" (default 768)
 * @returns {boolean}
 */
import { useEffect, useState } from 'react';

export default function useIsMobile(breakpoint = 768) {
  const getMatch = () =>
    typeof window !== 'undefined' &&
    window.matchMedia(`(max-width: ${breakpoint}px)`).matches;

  const [isMobile, setIsMobile] = useState(getMatch);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handler = (e) => setIsMobile(e.matches);

    if (mql.addEventListener) mql.addEventListener('change', handler);
    else mql.addListener(handler); // Safari < 14 fallback

    setIsMobile(mql.matches);

    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handler);
      else mql.removeListener(handler);
    };
  }, [breakpoint]);

  return isMobile;
}
